import { styled, alpha } from '@mui/material/styles';
import { Box, Button, Paper, Tab, Tabs } from '@mui/material';

/**
 * ページコンテナ
 */
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

/**
 * ページヘッダー
 */
export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

/**
 * pill型のタブバー
 *
 * @description
 * 白いカードの中にタブを並べ、選択中のタブだけを塗りつぶした角丸（pill）で示す。
 * 下線インジケーターは使わないため非表示にする。
 */
export const PillTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 'auto',
  marginBottom: theme.spacing(2.5),
  padding: '0 18px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(0.5),
  },
}));

/**
 * pill型のタブ1件
 */
export const PillTab = styled(Tab)(({ theme }) => ({
  minHeight: 'auto',
  minWidth: 'auto',
  padding: '11px 12px 10px',
  borderRadius: 0,
  borderBottom: '2px solid transparent',
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  transition: 'background-color .12s, color .12s',
  '&:hover': {
    backgroundColor: '#f8f7f5',
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    borderBottomColor: '#9f545c',
    color: theme.palette.text.primary,
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#f8f7f5',
  },
}));

/**
 * タブ配下のセクション見出し（見出し・説明文と右端アクション）
 */
export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(2),
}));

/**
 * タブ配下のコンテンツ領域
 */
export const TabPanelContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  position: 'relative',
  minHeight: 240,
}));

/**
 * パネル内のアクションバー（右寄せボタン群）
 */
export const PanelActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

/**
 * 月ナビゲーションエリア
 */
export const MonthNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

/**
 * ローディングオーバーレイ
 */
export const LoadingOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
}));

/**
 * 枠プレビュー用のカテゴリチップ行
 */
export const SlotPreviewRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0),
}));

/**
 * カテゴリ集計チップのラッパー
 */
export const CategoryCountWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
}));

/* ────────────────────────────────────────────
 * 月スコープ解放枠設定ビュー用
 * ──────────────────────────────────────────── */

/**
 * 戻るナビゲーション（「← 確定済みシフト / 2026/06」）
 */
export const BackNav = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

/**
 * 手順ガイドの外枠
 */
export const HowGuide = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  marginBottom: theme.spacing(2.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}));

/**
 * 手順ガイドの3カラムグリッド（狭幅では1カラム）
 */
export const HowStepGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

/**
 * 手順ガイドの1ステップ（最終要素以外は右に区切り線）
 */
export const HowStep = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 1.75),
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderRight: 'none',
  },
  [theme.breakpoints.down('md')]: {
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
}));

/**
 * ステップ番号バッジ（丸）
 *
 * @description
 * `$filled` が true のとき塗り（STEPヘッダー用）、false のとき淡色（手順ガイド用）。
 */
export const StepNumberBadge = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $filled?: boolean }>(({ theme, $filled }) => ({
  width: 26,
  height: 26,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 800,
  backgroundColor: $filled
    ? theme.palette.primary.main
    : alpha(theme.palette.primary.main, 0.12),
  color: $filled ? theme.palette.common.white : theme.palette.primary.dark,
}));

/**
 * STEPヘッダー（番号・見出し・右端アクション）
 */
export const StepHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.25),
  marginBottom: theme.spacing(1.75),
}));

/**
 * テンプレートカードの2カラムグリッド（狭幅では1カラム）
 */
export const TemplateCardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.75),
  marginBottom: theme.spacing(2.75),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

/**
 * テンプレートカード本体
 *
 * @description
 * `$selected` が true のとき枠線をプライマリ色にし、外側にハローを付ける。
 */
export const TemplateCardRoot = styled(Paper, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $selected?: boolean }>(({ theme, $selected }) => ({
  padding: theme.spacing(2.25, 2.5),
  border: `2px solid ${$selected ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  boxShadow: $selected ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
  height: '100%',
}));

/**
 * テンプレートカード内の区間バンド列（1時間分）
 */
export const TemplateBandRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(1.25),
}));

/**
 * テンプレートカード内の1区間バンド
 */
export const TemplateBand = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(0.75),
  padding: theme.spacing(0.875, 0.625),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(0.25),
}));

/**
 * 設定サマリの2カラムグリッド（狭幅では1カラム）
 */
export const SettingsRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.75),
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

/**
 * 設定サマリの1ブロック（🌐基本設定 / 👤個別上書き）
 */
export const SettingsBlock = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.25),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}));

/**
 * 補足メッセージボックス
 *
 * @description
 * モックの `.callout` に対応する淡色の情報ボックス。
 * MUIのAlertと異なりアイコン枠を持たないため、絵文字を先頭に置く用途で使う。
 */
export const Callout = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $tone?: 'blue' | 'amber' | 'green' }>(({ theme, $tone = 'blue' }) => {
  const palette = {
    blue: theme.palette.primary,
    amber: theme.palette.warning,
    green: theme.palette.success,
  }[$tone];

  return {
    display: 'flex',
    gap: theme.spacing(1.125),
    padding: theme.spacing(1.375, 1.75),
    borderRadius: theme.spacing(1),
    fontSize: 12,
    lineHeight: 1.65,
    backgroundColor: alpha(palette.main, 0.1),
    color: palette.dark,
  };
});

/**
 * 「＋ 個別上書きする先生を追加する」の破線ボタン
 */
export const AddRowButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1.25),
  padding: theme.spacing(1.125),
  fontWeight: 700,
  color: theme.palette.primary.dark,
  border: `1.5px dashed ${theme.palette.divider}`,
  '&:hover': {
    border: `1.5px dashed ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
}));

/**
 * 個別上書きの編集行（医師・テンプレート・削除の3カラム）
 */
export const OverrideRowEdit = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto',
  gap: theme.spacing(1),
  alignItems: 'center',
  padding: theme.spacing(1.125, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 'none',
  },
}));
